<template>
  <div v-if="requestUrl">
    <div class="flex flex-row flex-nowrap">
      <slot name="sideMenu" />
      <div class="dynamic-height overflow-y-auto flex-grow relative bg-white">
        <table class="w-full">
          <DataTableHeader
            :columns="columns"
            :filterStoreKey="filterStoreKey"
            @reload="reload"
          />
          <tbody>
            <template v-if="dataRows?.length > 0">
              <DataTableContent
                v-for="row in dataRows"
                :key="'dataTableRow_' + row.id"
                :columns="columns"
                :row="row"
                :actions="actions"
                :selectedRowKey="selectedRowKey"
                :canDelete="canDelete"
                :canUpdate="canUpdate"
                :deleteLabel="deleteLabel"
                @rowSelected="onRowSelected"
                @delete="showDeleteConfirmationDialog"
              />
            </template>
            <tr v-else-if="fetchingStatus !== 'running'">
              <td colspan="100">
                <NoResults
                  v-if="!hasFilters(filterStoreKey)"
                  class="!mt-10"
                  :hide-button="!emptyAction"
                  @click="emptyAction"
                  :buttonLabel="emptyActionLabel"
                  :image="emptyImage"
                  :title="emptyLabel ?? t('components.empty_states.no_data')"
                  :description="emptyDescription"
                />
                <NoResults v-else class="!mt-10" @click="clearFilters" />
              </td>
            </tr>
            <template v-if="fetchingStatus === 'running'">
              <DataTableLoader
                v-for="index in amountOfLoaders"
                :key="'loader_row_' + index"
                :columns="columns"
              />
            </template>
          </tbody>
        </table>
        <span
          ref="intersectTarget"
          class="h-[10px] w-full block relative bottom-32"
        />
      </div>
    </div>

    <ConfirmationModal
      :open="showDeleteConfirmation"
      @close="
        () => {
          showDeleteConfirmation = false;
          documentToBeDeleted = null;
        }
      "
      @confirm="handleDelete"
      :title="deleteConfirmation?.title"
      :confirmationLabel="deleteConfirmation?.confirm"
      :cancelLabel="deleteConfirmation?.cancel"
    >
      <template v-if="confirmationMessage">
        <p
          v-for="(message, index) in confirmationMessage"
          :key="index"
          class="m-0"
        >
          {{ message }}
        </p>
      </template>
    </ConfirmationModal>
  </div>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useIntersectionObserver, useElementVisibility } from "@vueuse/core";
import { useFilterStore } from "@/Tailwind/Stores/filters.js";
import { useEventBus } from "@/Tailwind/Mixins/useEventBus";

import DataTableHeader from "@/Tailwind/Components/Table/DataTableHeader.vue";
import DataTableContent from "@/Tailwind/Components/Table/DataTableContent.vue";
import DataTableLoader from "@/Tailwind/Components/Table/DataTableLoader.vue";
import NoResults from "@/Tailwind/Components/ListView/NoResults.vue";
import ConfirmationModal from "@/Tailwind/Components/ConfirmationModal.vue";

import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";

const props = defineProps({
  requestUrl: {
    type: String,
    required: true,
  },
  transformFilters: {
    type: Function,
  },
  columns: {
    type: Array, // Array of objects with following keys: key, label, type
    required: true,
  },
  actions: {
    type: Array, // Array of objects with following keys: key, label, type, onClick
    required: false,
    default: () => [],
  },
  perPage: {
    type: Number,
    required: false,
  },
  filterStoreKey: {
    type: String,
  },
  selectedRowKey: {
    type: String,
  },
  onRowSelected: {
    type: Function,
  },
  emptyLabel: {
    type: String,
  },
  emptyDescription: {
    type: String,
  },
  emptyImage: {
    type: String,
  },
  emptyActionLabel: {
    type: String,
  },
  emptyAction: {
    type: Function,
  },
  // An object of strings to use in place of the default labels
  // Possible keys are: title, confirm, cancel, message
  deleteConfirmation: {
    type: Object,
  },
  canDelete: {
    type: Boolean,
  },
  canUpdate: {
    type: Boolean,
  },
  deleteLabel: {
    type: String,
  },
});

const { t } = useI18n();

const emit = defineEmits(["delete"]);

const fetchingStatus = ref("");
const paginationObject = ref(null);
const dataRows = ref([]);

const perPage = props.perPage || 20;

const showDeleteConfirmation = ref(false);
const documentToBeDeleted = ref(null);

const showDeleteConfirmationDialog = (id) => {
  documentToBeDeleted.value = id;
  showDeleteConfirmation.value = true;
};

const handleDelete = () => {
  emit("delete", documentToBeDeleted.value);
  showDeleteConfirmation.value = false;
  documentToBeDeleted.value = null;
};

// Caculate amount of loaders here so it doesn't change on re-render
const amountOfLoaders = Math.ceil(Math.random() * 5);

// Filters
const filterStore = useFilterStore();
const { hasFilters } = filterStore;
const filterValues = storeToRefs(filterStore)[props.filterStoreKey];

// Cancellation logic
let controller = new AbortController();

const getData = () => {
  if (!props.requestUrl) {
    return;
  }

  if (
    paginationObject.value?.meta &&
    paginationObject.value?.meta.last_page ===
      paginationObject.value?.meta.current_page
  ) {
    return;
  }

  // Cancel any running requests before making a new one
  if (fetchingStatus.value === "running") {
    controller.abort();

    // Assign new controller to send new requests with different signal
    controller = new AbortController();
  }

  fetchingStatus.value = "running";

  const requestFilterValues =
    props.transformFilters && filterValues.value
      ? props.transformFilters(filterValues.value)
      : filterValues.value;

  axios
    .post(
      props.requestUrl,
      {
        per_page: perPage,
        page: paginationObject.value?.meta.current_page + 1,
        ...requestFilterValues,
      },
      {
        signal: controller.signal,
      }
    )
    .then((res) => {
      paginationObject.value = res.data;
      dataRows.value = [...dataRows.value, ...res.data.data];

      fetchingStatus.value = "success";

      // Give everything time to re-render before checking again
      setTimeout(() => {
        if (tableEndVisible.value) {
          getData();
        }
      }, 100);
    })
    .catch(() => {
      fetchingStatus.value = "error";
    });
};

const intersectTarget = ref();
const tableEndVisible = useElementVisibility(intersectTarget);

const { stop } = useIntersectionObserver(
  intersectTarget,
  ([{ isIntersecting }]) => {
    // Don't trigger multiple times while running
    if (isIntersecting && fetchingStatus.value !== "running") {
      getData();
    }
  }
);

onMounted(() => {
  // Initially fetch data because the interesect object will be invisible
  getData();
});

onUnmounted(() => {
  stop();
});

const reload = () => {
  reset();
  getData();
};

const reset = () => {
  paginationObject.value = null;
  dataRows.value = [];
  fetchingStatus.value = "";
};

watch(filterValues, () => reload(), { deep: true });

const { bus, emit: emitEventBus } = useEventBus();
const clearFilters = () => {
  emitEventBus("clearFilters");
};

watch(
  () => bus.value.get("reload-data-table"),
  () => {
    reload();
  }
);

const confirmationMessage = computed(() => {
  if (props.deleteConfirmation?.message) {
    return props.deleteConfirmation.message;
  }

  return [t("admin.settings.confirmation.message")];
});
</script>

<style lang="scss" scoped>
.dynamic-height {
  height: calc(100vh - 64px - 123px);
}
</style>
